import { ChangeEventHandler, FC, Fragment } from "react";
import { DateTime } from "luxon";
import { t } from "i18next";

import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import EllipsesMenuCoutas from "../molecules/EllipsesMenuCoutas";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { Fee, getFormatedPeriod } from "@/app/fees/fees";
import { TableLoader } from "../molecules/TableLoader";
import { PlusIcon } from "@heroicons/react/20/solid";
import ColumnText from "../molecules/ColumnText";
import { Empty } from "./Empty";

export type FeeTableListProps = {
  isLoading: boolean;
  onSearchChange: ChangeEventHandler<HTMLInputElement>;
  onEditFee: (fee: Fee) => void;
  search: string | undefined;
  fees: Array<Fee>;
  title: string;
  description: string;
  functionButtonCreate: () => void;
  functionButtonMatriculas: () => void;
  functionButtonPause?: (fee: Fee) => void;
  functionButtonMotivos: () => void;
  functionButtonLink?: (fee: Fee) => void;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  canCreateFee: boolean;
  canEditFee: boolean;
  canPauseFee;
};

export const FeeTableList: FC<FeeTableListProps> = ({
  isLoading,
  title,
  description,
  onEditFee,
  fees,
  onSearchChange,
  search,
  functionButtonCreate,
  functionButtonMatriculas,
  functionButtonPause,
  functionButtonMotivos,
  functionButtonLink,
  currentPage,
  pageSize,
  onPageChange,
  canCreateFee,
  canEditFee,
  canPauseFee,
}) => {
  const startIndex = (currentPage - 1) * pageSize;
  const currentFees = fees.slice(startIndex, startIndex + pageSize);

  return (
    <div className="flex-1 flex-col px-4 sm:px-6 lg:px-6">
      <div className="sm:flex sm:items-center">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0"></div>
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      <div className="sm:flex sm:items-center">
        <div className="sm:flex w-full lg:w-64">
          <InputWithCornerHint
            label={t("general.search")}
            span=""
            id=""
            name=""
            type=""
            placeholder={t("customer-to-gym-subscription.views.title")}
            error={false}
            className="w-full"
            value={search}
            onChange={onSearchChange}
          />
        </div>
        <div className="mt-8 space-y-3 sm:ml-auto sm:mt-4 sm:flex sm:items-center sm:space-x-4 ">
          {canCreateFee && (
            <button
              onClick={functionButtonCreate}
              type="button"
              className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
            >
              <PlusIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          )}
          <ButtonSecondary
            text={t("customer-to-gym-subscription.views.reason")}
            onClick={functionButtonMotivos}
            size="large"
          />
          <ButtonSecondary
            text={t("customer-to-gym-subscription.views.registrations")}
            onClick={functionButtonMatriculas}
            size="large"
          />
        </div>
      </div>

      <div className="border-b border-gray-300 mt-5"></div>

      <div className="flex flex-col flex-1">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <TableLoader columns={9} />
            ) : (
              <Fragment>
                {currentFees.length === 0 ? (
                  <Empty
                    texto={t("customer-to-gym-subscription.views.no_fee")}
                    onClick={functionButtonCreate}
                  />
                ) : (
                  <Fragment>
                    <table className="flex-1 min-w-full divide-y divide-gray-300">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                          >
                            {t("customer-to-gym-subscription.views.name")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12"
                          >
                            {t(
                              "customer-to-gym-subscription.views.description"
                            )}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12"
                          >
                            {t(
                              "customer-to-gym-subscription.views.renewal_period"
                            )}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                          >
                            {t("customer-to-gym-subscription.views.price")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                          >
                            {t("customer-to-gym-subscription.views.state")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12"
                          >
                            {t("customer-to-gym-subscription.views.time")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                          >
                            {t(
                              "customer-to-gym-subscription.views.number_of_people"
                            )}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                          >
                            {t("customer-to-gym-subscription.views.discount")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {currentFees.map((fee) => (
                          <tr key={fee.id}>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/12">
                              {fee.name}
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-2/12">
                              {fee.description}
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-2/12">
                              {getFormatedPeriod(
                                fee.interval,
                                fee.intervalCount
                              )}
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/12">
                              {`${fee.currency.toUpperCase()} ${(fee.price / 100).toFixed(2)}`}
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/12">
                              {fee.active == 1 ? "activa" : "inactiva"}
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/12">
                              <div>
                                <div className="has-tooltip">
                                  <div className="relative">
                                    <span
                                      className="tooltip rounded shadow bg-white text-gray-700 p-4 absolute top-0 left-0 transform -translate-y-full ml-20"
                                      style={{
                                        width: "12rem",
                                        top: "6rem",
                                        left: ".5rem",
                                        zIndex: "10",
                                        backgroundColor: "white",
                                        color: "gray",
                                        padding: "1rem",
                                        borderRadius: "0.25rem",
                                        boxShadow:
                                          "0 0.5rem 1rem rgba(0, 0, 0, 0.1)",
                                      }}
                                    >
                                      {fee.feeSchedules.map(
                                        (schedule, index) => (
                                          <p key={index}>
                                            {t(
                                              `days.${schedule.day.toLowerCase()}`
                                            )}{" "}
                                            {DateTime.fromFormat(
                                              schedule.startTime,
                                              "hh:mm:ss"
                                            ).toFormat("hh:mm")}{" "}
                                            -{" "}
                                            {DateTime.fromFormat(
                                              schedule.endTime,
                                              "hh:mm:ss"
                                            ).toFormat("hh:mm")}
                                          </p>
                                        )
                                      )}
                                    </span>
                                  </div>
                                  <span className="text-primary-500 hover:cursor-pointer">
                                    Ver horario
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/12">
                              {fee.people}
                            </td>
                            <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/12">
                              {`${fee.discount}%`}
                            </td>
                            <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                              <div className="flex items-center justify-end">
                                {canEditFee && (
                                  <a
                                    href="#"
                                    role="button"
                                    className="text-primary hover:text-primary-900"
                                    onClick={() => onEditFee(fee)}
                                  >
                                    {t("general.edit")}
                                    <span className="sr-only"></span>
                                  </a>
                                )}
                                <EllipsesMenuCoutas
                                  canPauseFee={canPauseFee}
                                  functionButtonPause={() => {
                                    if (functionButtonPause) {
                                      functionButtonPause(fee);
                                    }
                                  }}
                                  functionButtonModifyPause={() => {
                                    if (functionButtonPause) {
                                      functionButtonPause(fee);
                                    }
                                  }}
                                  functionButtonLink={() => {
                                    if (functionButtonLink) {
                                      functionButtonLink(fee);
                                    }
                                  }}
                                  activation={fee.active}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Fragment>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
